import React from "react";

import Topbar from "../components/topbar";
import AddBtn from "../components/ui/add-btn";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import UploadDocument from "../components/popup/upload-document";
import FolderName from "../components/popup/folder-name";
import MoveDocument, { FolderOption } from "../components/popup/move-document";
import ManageFolderShare from "../components/popup/manage-folder-share";
import InvoiceShareLink from "../components/popup/invoice-share-link";
import DocumentLinkSettings, { ShareLink } from "../components/popup/document-link-settings";
import { useMediaQuery, useTheme, Button, Box, Chip, Typography, Fab, Menu as MuiMenu, MenuItem, IconButton, Tooltip, Paper } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { Menu, Folder as FolderIcon, FolderLock, FolderOpen, ChevronRight, FolderPlus, Plus, Upload, ExternalLink, Copy, FolderInput, Pencil, Trash2, Share2, Link2, Ban, MoreHorizontal, RotateCcw } from "lucide-react";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";
import { useUser } from "../providers/user-provider";
import { useToast } from "../providers/toaster-provider";
import { usePermissions } from "../providers/permissions-provider";
import { useSettings } from "../providers/settings-provider";
import { useFeatures } from "../providers/features-provider";
import { defaultLinkName } from "../util/link-name";
import Cookies from "js-cookie";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { openPlanPrompt } from '../util/plan-required';

// Set independently of document-viewer.tsx - that page's own setup only
// runs once its module has actually been loaded, which this page (a
// separate route) can't rely on.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type FolderRow = {
    __type: 'folder';
    id: number;
    slug: string; // unused, present so folder/document rows share a shape for the table
    title: string;
    parentFolderId: number | null;
    createdAt: string;
    isSystem: boolean;
};

type LinkRow = ShareLink & {
    accessType: 'anonymous' | 'email_list';
    requiresNda: boolean;
    createdAt: string;
    isActive: boolean;
    lastViewedAt: string | null;
};

type DocumentRow = {
    __type: 'document';
    id: number;
    slug: string;
    title: string;
    type: 'pdf' | 'webpage';
    createdAt: string;
    source: string;
    folderId: number | null;
    lastViewedAt: string | null;
    links: LinkRow[];
    isInvoice: boolean;
    // Set for invoice documents - the invoice's own Share dialog
    // (invoice-share-permissions.tsx) edits its share link.
    invoiceId: number | null;
    invoiceNumber: string | null;
    // Invoice and report shares are opened through the Document's own slug
    // rather than a DocumentLink - these describe that link.
    sharedViaOwnSlug: boolean;
    accessType: 'anonymous' | 'email_list';
    expiresAt: string | null;
};

type Row = FolderRow | DocumentRow;

type SortState = { id: string; desc: boolean }[];

type RawFolder = { id: number; name: string; parentFolderId: number | null; createdAt: string; isSystem: boolean };

const REPORT_TYPE_LABELS: Record<string, string> = {
    'forecast': 'Shared Forecast',
    'forecast-vs-actual': 'Shared Forecast vs Actual',
};

// Tint used for every expanded area (links and viewers), as in the design.
const PANEL_BACKGROUND = 'color-mix(in srgb, var(--color-primary) 14%, transparent)';

// Seconds a single page view lasted; zero while the view is still open.
const durationSeconds = (v: any): number => (v.endedAt
    ? Math.max(0, Math.round((new Date(v.endedAt).getTime() - new Date(v.startedAt).getTime()) / 1000))
    : 0);

const formatSeconds = (secs: number): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

// "Last viewed 3 mins ago" style text for list rows.
const timeAgo = (iso: string | null, formatDate: (d: Date, showTime?: boolean) => string): string => {
    if (!iso) return 'Never viewed';
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return 'Last viewed just now';
    if (mins < 60) return `Last viewed ${mins} min${mins === 1 ? '' : 's'} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Last viewed ${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `Last viewed ${days} day${days === 1 ? '' : 's'} ago`;
    return `Last viewed ${formatDate(new Date(iso))}`;
};

const binDaysText = (doc: { daysLeft: number }): string => `Deletes in ${doc.daysLeft} day${doc.daysLeft === 1 ? '' : 's'}`;

const permissionLabel = (accessType: string): string => (accessType === 'anonymous' ? 'Anyone with link' : 'Restricted access');

type RowAction = { label: string; icon: React.ReactNode; onClick: () => void; disabled?: boolean; danger?: boolean };

// One "..." button per row that opens the row's actions, as in the design.
const RowActionsMenu: React.FC<{ actions: RowAction[] }> = ({ actions }) => {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    return (
        <>
            <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
                sx={{ border: '1px solid var(--color-primary)', borderRadius: 1, p: 0.25, color: 'var(--color-primary)' }}
            >
                <MoreHorizontal size={14} />
            </IconButton>
            <MuiMenu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                onClick={(e) => e.stopPropagation()}
            >
                {actions.map(action => (
                    <MenuItem
                        key={action.label}
                        disabled={action.disabled}
                        onClick={() => { setAnchor(null); action.onClick(); }}
                        sx={{ display: 'flex', gap: 1, fontSize: 13, color: action.danger ? '#d32f2f' : undefined }}
                    >
                        {action.icon}
                        {action.label}
                    </MenuItem>
                ))}
            </MuiMenu>
        </>
    );
};

// Column header with a sort toggle - same look as table-virtual.tsx's headers
// (label + round ▲/▼ button, teal when it's the active sort), so this page's
// table reads like every other table in the app instead of MRT's built-in
// sort arrows.
const SortableHeader: React.FC<{ label: string; columnId: string; sorting: SortState; onSort: (columnId: string) => void; alignRight?: boolean }> = ({ label, columnId, sorting, onSort, alignRight }) => {
    const active = sorting[0]?.id === columnId;
    const desc = active && sorting[0].desc;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: alignRight ? 'flex-end' : 'flex-start', width: '100%' }}>
            <Typography
                onClick={() => onSort(columnId)}
                sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'var(--color-primary)' } }}
            >
                {label}
            </Typography>
            <IconButton
                size="small"
                style={{
                    marginLeft: 4,
                    color: active ? 'var(--color-primary)' : '#B0B0B0',
                    backgroundColor: active ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
                    borderRadius: '50px',
                    width: 24,
                    height: 24,
                }}
                tabIndex={-1}
                aria-label="Sort"
                onClick={() => onSort(columnId)}
            >
                {active ? (
                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-primary)' }}>{desc ? '▼' : '▲'}</Typography>
                ) : (
                    <Typography sx={{ fontSize: '12px', opacity: 0.4, color: 'var(--color-text-muted)' }}>▲</Typography>
                )}
            </IconButton>
        </Box>
    );
};

// Shared look for every table in the page. Nested tables sit on the tinted panel, so
// their header and rows are transparent.
const standardTableProps = {
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    enableColumnActions: false,
    enableSorting: false,
    // Removes the header's "expand/collapse all" button (a double-chevron
    // icon) - each row's own single-chevron expand toggle is kept, this
    // only drops the extra bulk control nobody asked for.
    enableExpandAll: false,
    // The auto-generated expand column defaults to a size meant for a
    // normal (non-grid) layout - under layoutMode="grid" (used by the main
    // documents table below) its flex-grow scales with that default size,
    // so it was stretching into a huge empty strip instead of just wrapping
    // its chevron icon. Same flex-grow-vs-size quirk as the earlier Clients
    // page fix. grow: false stops it claiming leftover space at all.
    displayColumnDefOptions: {
        'mrt-row-expand': { size: 40, grow: false },
    },
    // The expand/collapse chevron's tooltip otherwise reads "Expand"/
    // "Collapse" on hover - the icon alone is enough, blanking these drops
    // the tooltip text without touching the icon or its click behavior.
    localization: { expand: '', collapse: '', expandAll: '', collapseAll: '' },
    initialState: { density: 'compact' as const },
    muiTablePaperProps: { sx: { boxShadow: 'none', background: 'transparent' } },
    muiTableHeadCellProps: { sx: { background: 'transparent', fontSize: 12, fontWeight: 600, py: 0.5, px: 1.25 } },
    muiTableBodyCellProps: { sx: { fontSize: 12, py: 0.25, px: 1.25 } },
    muiTableBodyRowProps: { sx: { backgroundColor: 'transparent' } },
};

// Bars for each page of the document, height = time spent on that page, with page
// numbers under the bars and time labels on the left. Hovering a bar shows a real
// MUI Tooltip (Popper-based, rendered into a portal at the document body) with the
// page, its time, and an actual thumbnail of that page - rendered client-side via
// react-pdf against the owner's own (never-watermarked) copy of the file. A portal is
// what makes this a proper floating tooltip instead of an in-flow box: positioning it
// with plain CSS (position: absolute) inside the row's own DOM subtree got clipped by
// the row-expand Collapse wrapper's overflow: hidden the moment it extended outside
// that box, and even once repositioned to stay inside those bounds, it still visually
// overlapped neighboring table rows instead of floating above them. The bars
// themselves are plain, always-rendered boxes - deliberately NOT nested inside
// react-pdf's <Document> (which only ever renders ONE of its loading/error/children
// props at a time), so the chart can never vanish just because the PDF is still
// loading or fails to load; only the tooltip's thumbnail depends on that.
const ViewerPageChart: React.FC<{ views: any[]; documentSlug: string; entityId: number | null }> = ({ views, documentSlug, entityId }) => {
    const secondsByPage = new Map<number, number>();
    views.forEach(v => {
        const page = Number(v.page);
        secondsByPage.set(page, (secondsByPage.get(page) ?? 0) + durationSeconds(v));
    });
    // The total page count is not stored, so the chart runs to the highest page seen.
    const totalPages = Math.max(...Array.from(secondsByPage.keys()), 1);
    const maxSeconds = Math.max(...Array.from(secondsByPage.values()), 1);
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    const yTicks = [1, 0.75, 0.5, 0.25, 0].map(f => formatSeconds(Math.round(maxSeconds * f)));
    const chartHeight = 120;

    const pdfFile = React.useMemo(() => {
        if (!entityId || !documentSlug) return null;
        const { url } = buildApiRequestConfig(routes.documents.ownerFile, [
            { param: 'slug', value: documentSlug },
            { param: 'entityId', value: entityId },
        ]);
        const token = Cookies.get('token');
        return { url, httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined };
    }, [documentSlug, entityId]);

    return (
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ width: 40, height: chartHeight, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: 10, color: 'var(--color-text-muted)' }}>
                    {yTicks.map(t => <span key={t}>{t}</span>)}
                </Box>
                <Box sx={{ flex: 1, height: chartHeight, display: 'flex', alignItems: 'flex-end', gap: '4px', borderBottom: '1px solid var(--color-text-muted)' }}>
                    {pages.map(page => {
                        const secs = secondsByPage.get(page) ?? 0;
                        return (
                            <Tooltip
                                key={page}
                                arrow
                                placement="top"
                                componentsProps={{
                                    tooltip: { sx: { background: '#fff', color: '#1a1a2e', p: 0, borderRadius: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.18)', maxWidth: 'none' } },
                                    arrow: { sx: { color: '#fff' } },
                                }}
                                title={
                                    <Box sx={{ width: 220 }}>
                                        {pdfFile && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', borderBottom: '1px solid #eef0f2' }}>
                                                <Document
                                                    file={pdfFile}
                                                    loading={<Typography sx={{ fontSize: 10, color: '#8a929b', py: 2, textAlign: 'center' }}>Loading…</Typography>}
                                                    error={<Typography sx={{ fontSize: 10, color: '#c00', py: 2, px: 1, textAlign: 'center' }}>Preview unavailable</Typography>}
                                                    noData={null}
                                                >
                                                    <Page
                                                        pageNumber={page}
                                                        width={220}
                                                        loading={null}
                                                        error={null}
                                                        renderTextLayer
                                                        renderAnnotationLayer
                                                    />
                                                </Document>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, px: 1, py: 0.75 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 8, color: '#8a929b', fontWeight: 600, letterSpacing: 0.3 }}>PAGE</Typography>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e' }}>{page} / {totalPages}</Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography sx={{ fontSize: 8, color: '#8a929b', fontWeight: 600, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>TIME SPENT</Typography>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e' }}>{formatSeconds(secs)}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                }
                            >
                                <Box sx={{
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    height: `${Math.max(secs ? 2 : 0, (secs / maxSeconds) * 100)}%`,
                                    background: 'var(--color-primary)',
                                    opacity: secs ? 1 : 0,
                                    cursor: secs ? 'pointer' : 'default',
                                }} />
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Box sx={{ width: 40 }} />
                <Box sx={{ flex: 1, display: 'flex', gap: '4px' }}>
                    {pages.map(page => (
                        <Typography key={page} sx={{ flex: '1 1 0', minWidth: 0, textAlign: 'center', fontSize: 10, color: 'var(--color-text-muted)' }}>
                            {page}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

// Viewers of one generated link, grouped by email - or, for an anonymous
// link, by IP address instead, so distinct anonymous visitors still show up
// as separate rows with their own viewing history, rather than all
// collapsing into a single "Anonymous" bucket. A view recorded before IP
// capture existed (or genuinely missing one) has no identity signal at all
// and falls back to that shared "Anonymous" bucket - the honest outcome
// when there's nothing to distinguish it by. Expanding a viewer shows their pages.
const LinkViewersPanel: React.FC<{ views: any[]; documentSlug: string; entityId: number | null }> = ({ views, documentSlug, entityId }) => {
    const { formatDate } = useSettings();
    const viewers = React.useMemo(() => {
        const groups = new Map<string, { viewer: string; lastViewed: string; seconds: number; views: any[] }>();
        views.forEach(v => {
            const key = v.viewerEmail || (v.ipAddress ? `ip:${v.ipAddress}` : 'Anonymous');
            const label = v.viewerEmail || (v.ipAddress ? `${v.ipAddress} (Anonymous)` : 'Anonymous');
            const group = groups.get(key) ?? { viewer: label, lastViewed: v.startedAt, seconds: 0, views: [] as any[] };
            if (new Date(v.startedAt) > new Date(group.lastViewed)) group.lastViewed = v.startedAt;
            group.seconds += durationSeconds(v);
            group.views.push(v);
            groups.set(key, group);
        });
        return Array.from(groups.values()).sort((a, b) => new Date(b.lastViewed).getTime() - new Date(a.lastViewed).getTime());
    }, [views]);

    return (
        <MaterialReactTable
            columns={[
                { accessorKey: 'viewer', header: 'Viewer' },
                { accessorKey: 'lastViewed', header: 'Last Date & Time Viewed', Cell: ({ cell }) => formatDate(new Date(cell.getValue<string>()), true) },
                { accessorKey: 'seconds', header: 'Viewing Duration', Cell: ({ cell }) => formatSeconds(cell.getValue<number>()) },
            ]}
            data={viewers}
            getRowId={(r: any) => r.viewer}
            enableExpanding
            getRowCanExpand={() => true}
            renderDetailPanel={({ row }: { row: any }) => <ViewerPageChart views={row.original.views} documentSlug={documentSlug} entityId={entityId} />}
            {...standardTableProps}
        />
    );
};

type DocumentLinksPanelProps = {
    doc: DocumentRow;
    entityId: number | null;
    canUpdate: boolean;
    onEditLink: (link: LinkRow) => void;
    onDeactivateLink: (link: LinkRow) => void;
    onOpenLink: (slug: string) => void;
    onCopyLink: (slug: string) => void;
    // Opens the invoice's own Share dialog - how an invoice share link is edited.
    onEditInvoiceShare: () => void;
};

// The generated links of one document. Loads the document's viewing history once;
// each link's viewers are filtered from it by linkId.
const DocumentLinksPanel: React.FC<DocumentLinksPanelProps> = ({ doc, entityId, canUpdate, onEditLink, onDeactivateLink, onOpenLink, onCopyLink, onEditInvoiceShare }) => {
    const { formatDate } = useSettings();
    const [fetchViews, setFetchViews] = useApi();
    const [views, setViews] = React.useState<any[] | null>(null);

    React.useEffect(() => {
        if (!entityId) return;
        setFetchViews(buildApiRequestConfig(routes.documents.analytics, [
            { param: 'slug', value: doc.slug },
            { param: 'entityId', value: entityId },
        ]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc.slug, entityId]);

    React.useEffect(() => {
        const { data, isLoading } = fetchViews;
        if (data && !isLoading) setViews(data.pageViews ?? []);
    }, [fetchViews]);

    const linkRows = React.useMemo(() => {
        const generated = (doc.links ?? [])
            .filter(l => l.isActive)
            .map(l => ({ ...l, isOwnSlug: false, viewsForLink: (views ?? []).filter(v => v.linkId === l.id) }));
        if (!doc.sharedViaOwnSlug) return generated;
        // The invoice/report share link itself - the Document's own slug,
        // set up from the invoice or report page's share dialog. Its views
        // are the ones recorded without a linkId.
        const ownSlugLink = {
            id: `own-${doc.slug}`,
            slug: doc.slug,
            // Same default every link gets: the document's name plus when its
            // link was generated (the document's own creation, for this one).
            nameLabel: defaultLinkName(doc.title, doc.createdAt),
            accessType: doc.accessType,
            requiresNda: false,
            createdAt: doc.createdAt,
            expiresAt: doc.expiresAt,
            isOwnSlug: true,
            viewsForLink: (views ?? []).filter(v => v.linkId == null),
        };
        return [ownSlugLink, ...generated];
    }, [doc, views]);

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', background: PANEL_BACKGROUND, px: 1.5, pb: 1, pt: 0.5 }}>
            {linkRows.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', py: 1 }}>No active links</Typography>
            ) : (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <MaterialReactTable
                        columns={[
                            {
                                accessorKey: 'nameLabel',
                                header: 'Link Name',
                                size: 200,
                                Cell: ({ cell }) => cell.getValue<string | null>() || '—',
                            },
                            {
                                accessorKey: 'slug',
                                header: 'Generated Link',
                                size: 240,
                                Cell: ({ row }) => (
                                    <Typography sx={{ fontSize: 13, color: 'var(--color-primary)', whiteSpace: 'normal', wordBreak: 'break-all' }}>
                                        {`${window.location.origin}/view/${row.original.slug}`}
                                    </Typography>
                                ),
                            },
                            { accessorKey: 'accessType', header: 'Permission Level', size: 150, Cell: ({ cell }) => permissionLabel(cell.getValue<string>()) },
                            { accessorKey: 'requiresNda', header: 'NDA Required?', size: 120, Cell: ({ cell }) => (cell.getValue<boolean>() ? 'Yes' : 'No') },
                            { accessorKey: 'createdAt', header: 'Date Created', size: 170, Cell: ({ cell }) => formatDate(new Date(cell.getValue<string>()), true) },
                            { accessorKey: 'expiresAt', header: 'Expiration Date', size: 150, Cell: ({ cell }) => (cell.getValue<string | null>() ? formatDate(new Date(cell.getValue<string>())) : '—') },
                            {
                                id: 'linkActions',
                                header: 'Actions',
                                size: 110,
                                muiTableHeadCellProps: { align: 'right' },
                                muiTableBodyCellProps: { align: 'right' },
                                Cell: ({ row }) => {
                                    const link = row.original as LinkRow;
                                    // The invoice/report share link is the Document's own
                                    // slug, not a DocumentLink. An invoice's opens the same
                                    // link settings modal in its invoice mode (Stripe
                                    // editable, the rest locked - invoice-share-link.tsx);
                                    // a report's has no settings to edit. Deleting either
                                    // means deleting the document itself (row-level Delete).
                                    const isOwnSlug = (row.original as any).isOwnSlug;
                                    return (
                                        <RowActionsMenu actions={[
                                            { label: 'Open link', icon: <ExternalLink size={14} />, onClick: () => onOpenLink(link.slug) },
                                            { label: 'Copy link', icon: <Copy size={14} />, onClick: () => onCopyLink(link.slug) },
                                            ...(isOwnSlug
                                                ? (doc.isInvoice && doc.invoiceId
                                                    ? [{ label: 'Edit', icon: <Pencil size={14} />, onClick: onEditInvoiceShare, disabled: !canUpdate }]
                                                    : [])
                                                : [
                                                    { label: 'Edit', icon: <Pencil size={14} />, onClick: () => onEditLink(link), disabled: !canUpdate },
                                                    { label: 'Delete', icon: <Ban size={14} />, onClick: () => onDeactivateLink(link), disabled: !canUpdate, danger: true },
                                                ]),
                                        ]} />
                                    );
                                },
                            },
                        ] as MRT_ColumnDef<any>[]}
                        data={linkRows}
                        getRowId={(r: any) => String(r.id)}
                        enableExpanding
                        getRowCanExpand={(row: any) => row.original.viewsForLink.length > 0}
                        renderDetailPanel={({ row }: { row: any }) => <LinkViewersPanel views={row.original.viewsForLink} documentSlug={doc.slug} entityId={entityId} />}
                        {...standardTableProps}
                    />
                </Box>
            )}
        </Box>
    );
};

// Deleted links for one document, shown in the bin under that document's own
// expandable row (the document "acts like a folder" for its deleted links,
// same idea as the bin listing deleted documents, just scoped to one
// document's links instead). Each link was never moved anywhere - deleting
// one just deactivates it (DocumentLinksPanel's own "Delete" action); this
// is literally the same list filtered to the inactive ones, with Restore in
// place of the rest of the actions.
const DeletedLinksPanel: React.FC<{ doc: DocumentRow; canUpdate: boolean; onRestoreLink: (linkId: number) => void }> = ({ doc, canUpdate, onRestoreLink }) => {
    const { formatDate } = useSettings();
    const deletedLinks = React.useMemo(() => doc.links.filter(l => !l.isActive), [doc.links]);

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', background: PANEL_BACKGROUND, px: 1.5, pb: 1, pt: 0.5 }}>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <MaterialReactTable
                    columns={[
                        {
                            accessorKey: 'nameLabel',
                            header: 'Link Name',
                            size: 200,
                            Cell: ({ cell }) => cell.getValue<string | null>() || '—',
                        },
                        {
                            accessorKey: 'slug',
                            header: 'Generated Link',
                            size: 240,
                            Cell: ({ row }) => (
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', whiteSpace: 'normal', wordBreak: 'break-all', textDecoration: 'line-through' }}>
                                    {`${window.location.origin}/view/${row.original.slug}`}
                                </Typography>
                            ),
                        },
                        { accessorKey: 'accessType', header: 'Permission Level', size: 150, Cell: ({ cell }) => permissionLabel(cell.getValue<string>()) },
                        { accessorKey: 'createdAt', header: 'Date Created', size: 170, Cell: ({ cell }) => formatDate(new Date(cell.getValue<string>()), true) },
                        {
                            id: 'linkActions',
                            header: 'Actions',
                            size: 110,
                            muiTableHeadCellProps: { align: 'right' },
                            muiTableBodyCellProps: { align: 'right' },
                            Cell: ({ row }) => (
                                <RowActionsMenu actions={[
                                    { label: 'Restore', icon: <RotateCcw size={14} />, onClick: () => onRestoreLink((row.original as LinkRow).id), disabled: !canUpdate },
                                ]} />
                            ),
                        },
                    ] as MRT_ColumnDef<any>[]}
                    data={deletedLinks}
                    getRowId={(r: any) => String(r.id)}
                    {...standardTableProps}
                />
            </Box>
        </Box>
    );
};

const Documents: React.FC = () => {
    const { user } = useUser();
    // The Documents add-on - see express-server
    // documents-addon-service.js. Without it the page shows a short
    // explanation instead (rendered at the bottom, after every hook).
    const { isVisibleInNav, isLoaded: featuresLoaded } = useFeatures();
    const hasDocumentsAddon = isVisibleInNav('documents');
    const { formatDate } = useSettings();
    const { can } = usePermissions();
    const canCreate = can('documents', 'create');
    const canUpdate = can('documents', 'update');
    const canDelete = can('documents', 'delete');
    const { showError, showSuccess } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();

    const [documents, setDocuments] = React.useState<DocumentRow[]>([]);
    const [folders, setFolders] = React.useState<RawFolder[]>([]);
    const [currentFolderId, setCurrentFolderId] = React.useState<number | null>(null);
    // Without the add-on the page is read-only: opening any create/update/
    // delete form shows the upgrade / add-on prompt instead (closing one -
    // a falsy value - always goes through).
    const gated = <T,>(setter: React.Dispatch<React.SetStateAction<T>>) => (value: React.SetStateAction<T>) => {
        if (value && !hasDocumentsAddon) {
            openPlanPrompt(user?.entityId, 'documents');
            return;
        }
        setter(value);
    };
    const [uploadOpen, setUploadOpenRaw] = React.useState(false);
    const setUploadOpen = gated(setUploadOpenRaw);
    const [folderNameOpen, setFolderNameOpenRaw] = React.useState<{ mode: 'create' | 'rename'; folderId?: number; initialName?: string } | null>(null);
    const setFolderNameOpen = gated(setFolderNameOpenRaw);
    const [fabMenuAnchor, setFabMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [moveTarget, setMoveTargetRaw] = React.useState<DocumentRow | null>(null);
    const setMoveTarget = gated(setMoveTargetRaw);
    const [deleteDocTarget, setDeleteDocTargetRaw] = React.useState<DocumentRow | null>(null);
    const setDeleteDocTarget = gated(setDeleteDocTargetRaw);
    const [deleteFolderTarget, setDeleteFolderTargetRaw] = React.useState<FolderRow | null>(null);
    const setDeleteFolderTarget = gated(setDeleteFolderTargetRaw);
    const [shareFolderTarget, setShareFolderTargetRaw] = React.useState<FolderRow | null>(null);
    const setShareFolderTarget = gated(setShareFolderTargetRaw);
    const [invoiceShareTarget, setInvoiceShareTargetRaw] = React.useState<{ id: number; invoiceNumber: string } | null>(null);
    const setInvoiceShareTarget = gated(setInvoiceShareTargetRaw);
    const [linkSettings, setLinkSettingsRaw] = React.useState<{ documentSlug: string; documentTitle: string; isInvoice: boolean; link: LinkRow | null } | null>(null);
    const setLinkSettings = gated(setLinkSettingsRaw);
    const [deactivateTarget, setDeactivateTargetRaw] = React.useState<{ documentSlug: string; link: LinkRow } | null>(null);
    const setDeactivateTarget = gated(setDeactivateTargetRaw);
    const [binDocs, setBinDocs] = React.useState<any[]>([]);
    const [purgeTarget, setPurgeTargetRaw] = React.useState<any | null>(null);
    const setPurgeTarget = gated(setPurgeTargetRaw);
    const [emptyBinOpen, setEmptyBinOpenRaw] = React.useState(false);
    const setEmptyBinOpen = gated(setEmptyBinOpenRaw);

    // API hooks
    const [fetchDocs, setFetchDocs] = useApi();
    const [fetchFolders, setFetchFolders] = useApi();
    const [deleteDoc, setDeleteDoc] = useApi();
    const [deleteFolderApi, setDeleteFolderApi] = useApi();
    const [deactivateApi, setDeactivateApi] = useApi();
    const [fetchBin, setFetchBin] = useApi();
    const [restoreApi, setRestoreApi] = useApi();
    const [purgeApi, setPurgeApi] = useApi();
    const [emptyBinApi, setEmptyBinApi] = useApi();
    const [restoreLinkApi, setRestoreLinkApi] = useApi();

    const reload = () => {
        setFetchDocs(prev => prev ? { ...prev } : prev);
        setFetchFolders(prev => prev ? { ...prev } : prev);
        setFetchBin(prev => prev ? { ...prev } : prev);
    };

    const getCurrentCompanyName = (): string => {
        if (!user?.entityId) return 'Company Name';
        try {
            const companiesCookie = Cookies.get('entities');
            const companies = companiesCookie ? JSON.parse(companiesCookie) : [];
            const currentCompany = companies.find((c: any) => c.entityId === user.entityId);
            return currentCompany?.entityName || 'Company Name';
        } catch {
            return 'Company Name';
        }
    };

    React.useEffect(() => {
        document.title = "FineAnts | Documents";
        // Read-only without the add-on - reads are allowed either way.
        if (user?.entityId && featuresLoaded) {
            setFetchDocs(buildApiRequestConfig(routes.documents.byEntity, [{ param: 'entityId', value: user.entityId }]));
            setFetchFolders(buildApiRequestConfig(routes.documentFolders.byEntity, [{ param: 'entityId', value: user.entityId }]));
            setFetchBin(buildApiRequestConfig(routes.documents.binByEntity, [{ param: 'entityId', value: user.entityId }]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.entityId, featuresLoaded, hasDocumentsAddon]);

    React.useEffect(() => {
        const { data: docsData, isLoading, error } = fetchDocs;
        if (docsData && !isLoading) {
            const list = docsData.documents ?? docsData;
            const transformed: DocumentRow[] = (list || []).map((doc: any) => ({
                __type: 'document',
                id: doc.id,
                slug: doc.slug,
                title: doc.title,
                type: doc.type,
                createdAt: doc.createdAt,
                source: doc.type === 'webpage'
                    ? (REPORT_TYPE_LABELS[doc.webpageUrl] || 'Shared Report')
                    : doc.invoice?.invoiceNumber ? `Invoice #${doc.invoice.invoiceNumber}` : 'Manual upload',
                folderId: doc.folderId ?? null,
                lastViewedAt: doc.lastViewedAt ?? null,
                isInvoice: !!(doc.invoiceId || doc.invoice),
                invoiceId: doc.invoiceId ?? doc.invoice?.id ?? null,
                invoiceNumber: doc.invoice?.invoiceNumber ?? null,
                // Report shares made since the Share button moved to the link
                // settings modal (share-report.tsx) come with a regular link,
                // so only older ones - no links at all - list their own slug.
                sharedViaOwnSlug: !!(doc.invoiceId || doc.invoice) || (doc.type === 'webpage' && !(doc.links ?? []).length),
                accessType: doc.accessType === 'email_list' ? 'email_list' : 'anonymous',
                expiresAt: doc.expiresAt ?? null,
                links: (doc.links ?? []).map((l: any) => ({
                    id: l.id,
                    slug: l.slug,
                    nameLabel: l.nameLabel ?? null,
                    accessType: l.accessType,
                    listMode: l.listMode === 'block' ? 'block' : 'allow',
                    entries: l.entries ?? [],
                    hasPassword: !!l.hasPassword,
                    requiresNda: !!l.requiresNda,
                    expiresAt: l.expiresAt ?? null,
                    createdAt: l.createdAt,
                    allowDownloads: l.allowDownloads !== false,
                    watermarkTemplate: l.watermarkTemplate ?? null,
                    watermarkOptions: l.watermarkOptions ?? null,
                    isActive: l.isActive !== false,
                    lastViewedAt: l.lastViewedAt ?? null,
                })),
            }));
            setDocuments(transformed);
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to fetch documents");
    }, [fetchDocs, showError]);
    React.useEffect(() => {
        const { data: foldersData, isLoading, error } = fetchFolders;
        if (foldersData && !isLoading) {
            const list = foldersData.folders ?? foldersData;
            setFolders((list || []).map((f: any) => ({ id: f.id, name: f.name, parentFolderId: f.parentFolderId, createdAt: f.createdAt, isSystem: !!f.isSystem })));
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to fetch folders");
    }, [fetchFolders, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteDoc;
        if (data && !isLoading) {
            showSuccess("Moved to Deleted Items");
            setDeleteDocTarget(null);
            reload();
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to delete document");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteDoc]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteFolderApi;
        if (data && !isLoading) {
            showSuccess("Folder deleted successfully");
            setDeleteFolderTarget(null);
            reload();
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to delete folder");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteFolderApi]);

    React.useEffect(() => {
        const { data, isLoading, error } = deactivateApi;
        if (data && !isLoading) {
            showSuccess("Link deleted");
            setDeactivateTarget(null);
            reload();
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to delete link");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deactivateApi]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchBin;
        if (data && !isLoading) setBinDocs(data.documents ?? []);
        if (error) showError(typeof error === 'string' ? error : "Failed to load the bin");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchBin]);

    React.useEffect(() => {
        const { data, isLoading, error } = restoreApi;
        if (data && !isLoading) {
            showSuccess("Document restored");
            reload();
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to restore document");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restoreApi]);

    React.useEffect(() => {
        const { data, isLoading, error } = purgeApi;
        if (data && !isLoading) {
            showSuccess("Document deleted permanently");
            setPurgeTarget(null);
            reload();
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to delete document");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [purgeApi]);

    React.useEffect(() => {
        const { data, isLoading, error } = emptyBinApi;
        if (data && !isLoading) {
            showSuccess("Bin emptied");
            setEmptyBinOpen(false);
            reload();
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to empty the bin");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emptyBinApi]);

    React.useEffect(() => {
        const { data, isLoading, error } = restoreLinkApi;
        if (data && !isLoading) {
            showSuccess("Link restored");
            reload();
        }
        if (error) showError(typeof error === 'string' ? error : "Failed to restore link");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restoreLinkApi]);

    const handleRestore = (slug: string) => {
        setRestoreApi({
            ...buildApiRequestConfig(routes.documents.restore, [{ param: 'slug', value: slug }]),
            body: { entityId: user?.entityId },
        });
    };

    // Deleting a link (DocumentLinksPanel's "Delete") just deactivates it via
    // the same update-link endpoint deactivation already used - restoring is
    // the same endpoint with isActive flipped back, no separate backend
    // concept needed. The link was never moved anywhere; it's still attached
    // to its original document, just hidden from the live links list
    // (DocumentLinksPanel filters to isActive only) until restored.
    const handleRestoreLink = (documentSlug: string, linkId: number) => {
        setRestoreLinkApi({
            ...buildApiRequestConfig(routes.documents.updateLink, [
                { param: 'slug', value: documentSlug },
                { param: 'linkId', value: linkId },
            ]),
            body: { entityId: user?.entityId, isActive: true },
        });
    };

    const handleConfirmPurge = () => {
        if (!purgeTarget) return;
        setPurgeApi({
            ...buildApiRequestConfig(routes.documents.purgeBin, [{ param: 'slug', value: purgeTarget.slug }]),
            body: { entityId: user?.entityId },
        });
    };

    const handleConfirmEmptyBin = () => {
        setEmptyBinApi({
            ...buildApiRequestConfig(routes.documents.emptyBin, []),
            body: { entityId: user?.entityId },
        });
    };

    const handleConfirmDeleteDoc = () => {
        if (!deleteDocTarget) return;
        setDeleteDoc({
            ...buildApiRequestConfig(routes.documents.delete, [{ param: 'slug', value: deleteDocTarget.slug }]),
            body: { entityId: user?.entityId },
        });
    };

    const handleConfirmDeleteFolder = () => {
        if (!deleteFolderTarget) return;
        setDeleteFolderApi({
            ...buildApiRequestConfig(routes.documentFolders.delete, [{ param: 'id', value: deleteFolderTarget.id }]),
            body: { entityId: user?.entityId },
        });
    };

    const handleConfirmDeactivate = () => {
        if (!deactivateTarget) return;
        setDeactivateApi({
            ...buildApiRequestConfig(routes.documents.updateLink, [
                { param: 'slug', value: deactivateTarget.documentSlug },
                { param: 'linkId', value: deactivateTarget.link.id },
            ]),
            body: { entityId: user?.entityId, isActive: false },
        });
    };

    const viewerUrl = (slug: string) => `${window.location.origin}/view/${slug}`;
    const openLink = (slug: string) => window.open(viewerUrl(slug), '_blank', 'noopener,noreferrer');
    const copyLink = (slug: string) => {
        navigator.clipboard.writeText(viewerUrl(slug));
        showSuccess('Link copied to clipboard');
    };

    // A system folder itself (Invoices, Summaries, Forecasts, Forecast vs
    // Actual, Deleted Items) is filled by the system only - no uploads into
    // it. Root and every user-created folder, including ones nested under a
    // system folder, are open. Mirrors isSystemFolder in document-service.js.
    const systemFolderIds = React.useMemo(() => new Set(folders.filter(f => f.isSystem).map(f => f.id)), [folders]);
    const isInSystemFolder = React.useCallback(
        (folderId: number | null): boolean => folderId !== null && systemFolderIds.has(folderId),
        [systemFolderIds]
    );

    const foldersById = React.useMemo(() => new Map(folders.map(f => [f.id, f])), [folders]);

    // Full "Parent / Child" path of a folder, for the flat folder pickers.
    const pathFor = React.useCallback((id: number): string => {
        const names: string[] = [];
        let current: RawFolder | undefined = foldersById.get(id);
        let guard = 0;
        while (current && guard < 10) {
            names.unshift(current.name);
            current = current.parentFolderId ? foldersById.get(current.parentFolderId) : undefined;
            guard++;
        }
        return names.join(' / ');
    }, [foldersById]);

    // The nearest system folder at or above folderId - for a system-filed
    // document, the folder it belongs to even after it's been moved into a
    // subfolder of it. Mirrors getHomeSystemFolder in document-service.js.
    const homeSystemFolderOf = React.useCallback((folderId: number | null): RawFolder | null => {
        let current = folderId !== null ? foldersById.get(folderId) : undefined;
        let guard = 0;
        while (current && guard < 10) {
            if (current.isSystem) return current;
            current = current.parentFolderId ? foldersById.get(current.parentFolderId) : undefined;
            guard++;
        }
        return null;
    }, [foldersById]);

    const isWithinFolder = React.useCallback((folderId: number, ancestorId: number): boolean => {
        let current = foldersById.get(folderId);
        let guard = 0;
        while (current && guard < 10) {
            if (current.id === ancestorId) return true;
            current = current.parentFolderId ? foldersById.get(current.parentFolderId) : undefined;
            guard++;
        }
        return false;
    }, [foldersById]);

    // Upload/move destinations for ordinary documents: every non-system
    // folder (root is offered by the popups themselves).
    const folderPaths = React.useMemo<FolderOption[]>(
        () => folders.filter(f => !f.isSystem).map(f => ({ id: f.id, path: pathFor(f.id) })),
        [folders, pathFor]
    );

    // A system-filed document (invoice, report share) can only move within
    // its home system folder - that folder itself and anything under it, no
    // root. Same rule as updateDocument in document-service.js.
    const moveHome = moveTarget && (moveTarget.isInvoice || moveTarget.type === 'webpage')
        ? homeSystemFolderOf(moveTarget.folderId)
        : null;
    const moveFolderOptions = React.useMemo<FolderOption[]>(() => (moveHome
        ? folders.filter(f => isWithinFolder(f.id, moveHome.id)).map(f => ({ id: f.id, path: pathFor(f.id) }))
        : folderPaths), [moveHome, folders, folderPaths, isWithinFolder, pathFor]);

    // Breadcrumb trail from root down to the current folder
    const breadcrumbs = React.useMemo(() => {
        const byId = new Map(folders.map(f => [f.id, f]));
        const trail: { id: number; name: string }[] = [];
        let current = currentFolderId ? byId.get(currentFolderId) : undefined;
        let guard = 0;
        while (current && guard < 10) {
            trail.unshift({ id: current.id, name: current.name });
            current = current.parentFolderId ? byId.get(current.parentFolderId) : undefined;
            guard++;
        }
        return trail;
    }, [folders, currentFolderId]);

    // Newest view of anything inside each folder, including its subfolders
    const folderLastViewed = React.useMemo(() => {
        const byId = new Map(folders.map(f => [f.id, f]));
        const newest = new Map<number, string>();
        documents.forEach(doc => {
            if (!doc.lastViewedAt || doc.folderId == null) return;
            let folderId: number | null = doc.folderId;
            let guard = 0;
            while (folderId && guard < 10) {
                const current = newest.get(folderId);
                if (!current || new Date(doc.lastViewedAt) > new Date(current)) newest.set(folderId, doc.lastViewedAt);
                folderId = byId.get(folderId)?.parentFolderId ?? null;
                guard++;
            }
        });
        return newest;
    }, [folders, documents]);

    const currentDepth = breadcrumbs.length;
    const MAX_FOLDER_DEPTH = 5;

    // The 'Deleted Items' system folder shows the entity's bin instead of its contents.
    const binFolder = folders.find(f => f.isSystem && f.name === 'Deleted Items' && f.parentFolderId === null);
    const inBin = !!binFolder && currentFolderId === binFolder.id;

    // Uploads are allowed at root and in any non-system folder; new folders
    // anywhere but the bin (system folders included).
    const canUploadHere = !inBin && !isInSystemFolder(currentFolderId);
    const canCreateFolderHere = !inBin;

    // Every active document is listed, shared or not - a freshly uploaded
    // document has no link yet, and its row (via "New link") is the only
    // place one can be created, so hiding unshared documents made new
    // uploads vanish with no way to share them.
    const listedDocuments = documents;

    // Direct children of each folder, for the Contents column. Deleted Items
    // counts the bin's documents, since that's what opening it lists.
    const folderCounts = React.useMemo(() => {
        const counts = new Map<number, { folders: number; documents: number }>();
        const entry = (id: number) => {
            if (!counts.has(id)) counts.set(id, { folders: 0, documents: 0 });
            return counts.get(id)!;
        };
        folders.forEach(f => { if (f.parentFolderId !== null) entry(f.parentFolderId).folders++; });
        listedDocuments.forEach(d => { if (d.folderId !== null) entry(d.folderId).documents++; });
        if (binFolder) entry(binFolder.id).documents = binDocs.length;
        return counts;
    }, [folders, listedDocuments, binFolder, binDocs]);

    const [sorting, setSorting] = React.useState<SortState>([{ id: 'name', desc: false }]);

    const rows: Row[] = React.useMemo(() => {
        const folderRows: FolderRow[] = folders
            .filter(f => f.parentFolderId === currentFolderId)
            .map(f => ({ __type: 'folder', id: f.id, slug: `folder-${f.id}`, title: f.name, parentFolderId: f.parentFolderId, createdAt: f.createdAt, isSystem: f.isSystem }));
        const documentRows = listedDocuments.filter(d => d.folderId === currentFolderId);
        return [...folderRows, ...documentRows];
    }, [folders, listedDocuments, currentFolderId]);

    // Deleting a link (not a whole document) never moves anything - the
    // document "acts like a folder" for its deleted links: in the bin, each
    // document that has at least one deleted link gets its own expandable
    // row (independent of whether the document itself is active or in the
    // bin), listing just those links with a Restore action.
    const documentsWithDeletedLinks = React.useMemo(
        () => documents.filter(d => d.links.some(l => !l.isActive)),
        [documents]
    );

    // Clicking the active column flips its direction; a different column
    // starts ascending - same toggle as table-virtual.tsx.
    const handleSort = React.useCallback((columnId: string) => {
        setSorting(prev => [{ id: columnId, desc: prev[0]?.id === columnId ? !prev[0].desc : false }]);
    }, []);

    // Sorted here rather than by the table itself so folders always stay
    // above documents whichever column or direction is chosen.
    const tableData = React.useMemo(() => {
        // folderCount/documentCount are null for document rows - only folders
        // have contents.
        const data = inBin ? [
            ...binDocs.map(d => ({ rowId: `bin-${d.slug}`, __type: 'bin', name: d.title, typeLabel: 'Document', lastViewedAt: null, folderCount: null, documentCount: null, orig: d })),
            ...documentsWithDeletedLinks.map(d => ({ rowId: `bin-links-${d.slug}`, __type: 'bin-link-group', name: d.title, typeLabel: 'Document', lastViewedAt: null, folderCount: null, documentCount: null, orig: d })),
        ] : rows.map(r => {
            if (r.__type === 'folder') {
                const count = folderCounts.get(r.id) ?? { folders: 0, documents: 0 };
                return {
                    rowId: `folder-${r.id}`, __type: 'folder', id: r.id, name: r.title, isSystem: r.isSystem,
                    typeLabel: r.isSystem ? 'System Folder' : 'Folder',
                    lastViewedAt: folderLastViewed.get(r.id) ?? null,
                    folderCount: count.folders, documentCount: count.documents, orig: r,
                };
            }
            return { rowId: `doc-${r.slug}`, __type: 'document', name: r.title, typeLabel: 'Document', lastViewedAt: r.lastViewedAt, folderCount: null, documentCount: null, orig: r };
        });

        const { id, desc } = sorting[0] ?? { id: 'name', desc: false };
        const compare = (a: any, b: any): number => {
            if (id === 'lastViewedAt') {
                // Never-viewed rows sort as the oldest.
                return (a.lastViewedAt ? new Date(a.lastViewedAt).getTime() : 0) - (b.lastViewedAt ? new Date(b.lastViewedAt).getTime() : 0);
            }
            if (id === 'folderCount' || id === 'documentCount') return (a[id] ?? 0) - (b[id] ?? 0);
            if (id === 'typeLabel') return a.typeLabel.localeCompare(b.typeLabel);
            return a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
        };
        const isFolder = (r: any) => (r.__type === 'folder' ? 0 : 1);
        return [...data].sort((a, b) => isFolder(a) - isFolder(b)
            || (desc ? -compare(a, b) : compare(a, b))
            || a.name.localeCompare(b.name));
    }, [rows, folderLastViewed, folderCounts, inBin, binDocs, documentsWithDeletedLinks, sorting]);

    const documentColumns = React.useMemo<MRT_ColumnDef<any>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            Header: () => <SortableHeader label="Name" columnId="name" sorting={sorting} onSort={handleSort} />,
            size: 300,
            Cell: ({ row }) => {
                const r = row.original;
                if (r.__type === 'folder') {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {r.isSystem ? <FolderLock size={16} color="var(--color-text-muted)" /> : <FolderIcon size={16} color="var(--color-primary)" />}
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.name}</Typography>
                        </Box>
                    );
                }
                if (r.__type === 'bin-link-group') {
                    const deletedCount = (r.orig.links as LinkRow[]).filter(l => !l.isActive).length;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Link2 size={16} color="var(--color-text-muted)" />
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.name}</Typography>
                            <Chip label={`${deletedCount} deleted link${deletedCount === 1 ? '' : 's'}`} size="small" sx={{ fontSize: 10, height: 18, background: 'var(--color-container-background)', color: 'var(--color-text-muted)' }} />
                        </Box>
                    );
                }
                return <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-primary)' }}>{r.name}</Typography>;
            },
        },
        {
            accessorKey: 'typeLabel',
            header: 'Type',
            Header: () => <SortableHeader label="Type" columnId="typeLabel" sorting={sorting} onSort={handleSort} />,
            size: 140,
            Cell: ({ row }) => (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{row.original.typeLabel}</Typography>
            ),
        },
        {
            accessorKey: 'folderCount',
            header: 'Folders',
            Header: () => <SortableHeader label="Folders" columnId="folderCount" sorting={sorting} onSort={handleSort} />,
            size: 110,
            Cell: ({ row }) => (row.original.folderCount === null ? null : (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{row.original.folderCount}</Typography>
            )),
        },
        {
            accessorKey: 'documentCount',
            header: 'Documents',
            Header: () => <SortableHeader label="Documents" columnId="documentCount" sorting={sorting} onSort={handleSort} />,
            size: 120,
            Cell: ({ row }) => (row.original.documentCount === null ? null : (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{row.original.documentCount}</Typography>
            )),
        },
        {
            accessorKey: 'lastViewedAt',
            header: 'Last Viewed',
            Header: () => <SortableHeader label="Last Viewed" columnId="lastViewedAt" sorting={sorting} onSort={handleSort} alignRight />,
            size: 200,
            muiTableHeadCellProps: { align: 'right' },
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {row.original.__type === 'bin' ? binDaysText(row.original.orig)
                        : row.original.__type === 'bin-link-group' ? ''
                        : timeAgo(row.original.lastViewedAt, formatDate)}
                </Typography>
            ),
        },
        {
            id: 'actions',
            header: 'Actions',
            size: 80,
            Header: () => <Typography>Actions</Typography>,
            muiTableHeadCellProps: { align: 'right' },
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => {
                const r = row.original;
                // No row-level action here - restoring happens per-link
                // inside the expanded panel below.
                if (r.__type === 'bin-link-group') return null;
                if (r.__type === 'bin') {
                    return (
                        <RowActionsMenu actions={[
                            { label: 'Restore', icon: <RotateCcw size={14} />, onClick: () => handleRestore(r.orig.slug), disabled: !canUpdate },
                            { label: 'Delete permanently', icon: <Trash2 size={14} />, onClick: () => setPurgeTarget(r.orig), disabled: !canDelete, danger: true },
                        ]} />
                    );
                }
                if (r.__type === 'folder') {
                    // System folders (Invoices, Summaries, Forecasts, Forecast
                    // vs Actual, Deleted Items) get no action button at all -
                    // they can never be renamed or deleted
                    // (document-folder-service.js), and aren't shared as a whole.
                    if (r.isSystem) return null;
                    return (
                        <RowActionsMenu actions={[
                            { label: 'Rename', icon: <Pencil size={14} />, onClick: () => setFolderNameOpen({ mode: 'rename', folderId: r.id, initialName: r.name }), disabled: !canUpdate },
                            { label: 'Share folder', icon: <Share2 size={14} />, onClick: () => setShareFolderTarget(r.orig), disabled: !canUpdate },
                            { label: 'Delete', icon: <Trash2 size={14} />, onClick: () => setDeleteFolderTarget(r.orig), disabled: !canDelete, danger: true },
                        ]} />
                    );
                }
                return (
                    <RowActionsMenu actions={[
                        // An invoice document already has its own one link
                        // (the Document's own slug, edited via the invoice's
                        // share dialog, invoice-share-permissions.tsx) -
                        // restricted to that single link, not offered a
                        // second, separate DocumentLink here. Every other
                        // document can generate (multiple) links.
                        ...(!r.orig.isInvoice ? [{ label: 'New link', icon: <Link2 size={14} />, onClick: () => setLinkSettings({ documentSlug: r.orig.slug, documentTitle: r.orig.title, isInvoice: r.orig.isInvoice, link: null }), disabled: !canUpdate }] : []),
                        // Every document can be moved - the popup only offers
                        // the destinations its kind allows (see moveFolderOptions).
                        { label: 'Move to folder', icon: <FolderInput size={14} />, onClick: () => setMoveTarget(r.orig), disabled: !canUpdate },
                        { label: 'Delete', icon: <Trash2 size={14} />, onClick: () => setDeleteDocTarget(r.orig), disabled: !canDelete, danger: true },
                    ]} />
                );
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [formatDate, canUpdate, canDelete, sorting, handleSort]);

    return (
        <div style={{ width: '100%' }}>
            <UploadDocument
                open={uploadOpen}
                entityId={user?.entityId ?? null}
                folderId={currentFolderId}
                folders={folderPaths}
                onClose={() => setUploadOpen(false)}
                onUploaded={reload}
            />
            <DocumentLinkSettings
                open={!!linkSettings}
                documentSlug={linkSettings?.documentSlug ?? null}
                documentTitle={linkSettings?.documentTitle ?? ''}
                isInvoice={!!linkSettings?.isInvoice}
                entityId={user?.entityId ?? null}
                link={linkSettings?.link ?? null}
                onClose={() => setLinkSettings(null)}
                onSaved={() => {
                    setLinkSettings(null);
                    reload();
                }}
            />
            <FolderName
                open={!!folderNameOpen}
                mode={folderNameOpen?.mode || 'create'}
                folderId={folderNameOpen?.folderId}
                initialName={folderNameOpen?.initialName}
                parentFolderId={currentFolderId}
                entityId={user?.entityId ?? null}
                onClose={() => setFolderNameOpen(null)}
                onSaved={reload}
            />
            <MoveDocument
                open={!!moveTarget}
                onClose={() => setMoveTarget(null)}
                onMoved={reload}
                entityId={user?.entityId ?? null}
                documentSlug={moveTarget?.slug ?? null}
                documentTitle={moveTarget?.title ?? ''}
                currentFolderId={moveTarget?.folderId ?? null}
                folders={moveFolderOptions}
                allowRoot={!moveHome}
            />
            {/* Edit on an invoice's share link - same dialog as the Invoices
                page's "Share / Permissions" action. */}
            {invoiceShareTarget && (
                <InvoiceShareLink
                    open
                    invoiceId={invoiceShareTarget.id}
                    invoiceNumber={invoiceShareTarget.invoiceNumber}
                    entityId={user?.entityId ?? null}
                    onClose={() => {
                        setInvoiceShareTarget(null);
                        reload();
                    }}
                />
            )}
            <ManageFolderShare
                open={!!shareFolderTarget}
                folderId={shareFolderTarget?.id ?? null}
                folderName={shareFolderTarget?.title ?? ''}
                entityId={user?.entityId ?? null}
                onClose={() => setShareFolderTarget(null)}
            />
            <DeleteModal
                open={!!deleteDocTarget}
                message="Delete Document?"
                description="This action will delete the selected document. It will be moved to the Deleted Items folder, where it will remain for 30 days before permanent removal. Any links associated with this document will be revoked and will no longer be usable unless the document is restored."
                red
                onCancel={() => setDeleteDocTarget(null)}
                onConfirm={handleConfirmDeleteDoc}
            />
            <DeleteModal
                open={!!deleteFolderTarget}
                message="Delete Folder?"
                description="This action will delete the selected folder and all of its contents. The folder will be moved to the Deleted Items folder, where it will remain for 30 days before permanent removal. Any links associated with documents in this folder will be revoked and will no longer be usable unless the folder or its contents are restored."
                red
                onCancel={() => setDeleteFolderTarget(null)}
                onConfirm={handleConfirmDeleteFolder}
            />
            <DeleteModal
                open={!!deactivateTarget}
                message="Delete Link?"
                description="This action will revoke access to the generated link. The link will be moved to the Deleted Items folder, where it will remain for 30 days before permanent removal. Guest access through this link will no longer be available unless the link is restored."
                red
                onCancel={() => setDeactivateTarget(null)}
                onConfirm={handleConfirmDeactivate}
            />
            <DeleteModal
                open={!!purgeTarget}
                message="Delete Permanently?"
                description="This removes the file, its share links and its viewing history for good. It can't be undone."
                red
                onCancel={() => setPurgeTarget(null)}
                onConfirm={handleConfirmPurge}
            />
            <DeleteModal
                open={emptyBinOpen}
                message="Empty Bin?"
                description={`All ${binDocs.length} document(s) in the bin will be deleted permanently. This can't be undone.`}
                red
                onCancel={() => setEmptyBinOpen(false)}
                onConfirm={handleConfirmEmptyBin}
            />
            <Topbar
                title="Documents"
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setSidebarOpen(true)}
                            sx={{
                                color: '#fff',
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)',
                                '&:hover': { backgroundColor: 'var(--color-primary)' }
                            }}
                        >
                            <Menu size={20} color="#fff" />
                        </Button>
                    )
                }
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                    {!isMobile && (
                        <>
                            {canCreateFolderHere && (
                                <Button
                                    variant="outlined"
                                    startIcon={<FolderPlus size={16} />}
                                    disabled={!canCreate || currentDepth >= MAX_FOLDER_DEPTH}
                                    onClick={() => setFolderNameOpen({ mode: 'create' })}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}
                                >
                                    New Folder
                                </Button>
                            )}
                            {inBin && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    disabled={!canDelete || binDocs.length === 0}
                                    onClick={() => setEmptyBinOpen(true)}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}
                                >
                                    Empty bin
                                </Button>
                            )}
                            {canUploadHere && (
                                <AddBtn disabled={!canCreate} onClick={() => setUploadOpen(true)} label="Add Document" />
                            )}
                        </>
                    )}
                </Box>
            </Topbar>
            {/* Read-only without the add-on. */}
            {featuresLoaded && !hasDocumentsAddon && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mx: '10px', mt: '10px', px: 2, py: 1, borderRadius: 2, background: 'var(--color-container-background)' }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Please upgrade to access</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => openPlanPrompt(user?.entityId, 'documents')}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', background: 'var(--color-primary)' }}
                    >
                        Upgrade
                    </Button>
                </Box>
            )}
            { breadcrumbs.length > 0 &&
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, pt: 1.5 }}>
                    <Box
                        onClick={() => setCurrentFolderId(null)}
                        sx={{
                            fontSize: 13, fontWeight: currentFolderId === null ? 700 : 500,
                            color: currentFolderId === null ? 'var(--color-default-text)' : 'var(--color-primary)',
                            cursor: currentFolderId === null ? 'default' : 'pointer',
                            '&:hover': currentFolderId === null ? {} : { textDecoration: 'underline' }
                        }}
                    >
                        {getCurrentCompanyName()}
                    </Box>
                    {breadcrumbs.map((b, idx) => (
                        <React.Fragment key={b.id}>
                            <ChevronRight size={14} color="var(--color-text-muted)" />
                            <Typography
                                onClick={() => setCurrentFolderId(b.id)}
                                sx={{
                                    fontSize: 13, fontWeight: idx === breadcrumbs.length - 1 ? 700 : 500,
                                    color: idx === breadcrumbs.length - 1 ? 'var(--color-default-text)' : 'var(--color-primary)',
                                    cursor: idx === breadcrumbs.length - 1 ? 'default' : 'pointer',
                                    '&:hover': idx === breadcrumbs.length - 1 ? {} : { textDecoration: 'underline' },
                                }}
                            >
                                {b.name}
                            </Typography>
                        </React.Fragment>
                    ))}
                </Box>
            }
            <div style={{ padding: '10px' }}>
                {tableData.length === 0 && (
                    <Paper sx={{ width: '100%', borderRadius: 0, background: 'var(--color-page-background)', p: 2, boxSizing: 'border-box' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6, textAlign: 'center' }}>
                            {inBin
                                ? <Trash2 size={40} strokeWidth={1.5} color="var(--color-text-muted)" />
                                : <FolderOpen size={40} strokeWidth={1.5} color="var(--color-text-muted)" />}
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                {inBin ? 'The bin is empty' : 'This folder is empty'}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', maxWidth: 360 }}>
                                {inBin
                                    ? 'Deleted documents and links show up here for 30 days before they are removed for good.'
                                    : canUploadHere
                                        ? 'Upload a document or create a folder to get started.'
                                        : 'Documents shared from elsewhere in the app will show up here. You can still create folders to organize this space.'}
                            </Typography>
                        </Box>
                    </Paper>
                )}
                {tableData.length > 0 && (
                    <Paper sx={{ width: '100%', borderRadius: 0, background: 'var(--color-page-background)', p: 2, boxSizing: 'border-box' }}>
                    <MaterialReactTable
                        columns={documentColumns}
                        data={tableData}
                        layoutMode="grid"
                        getRowId={(row: any) => row.rowId}
                        enableExpanding
                        muiDetailPanelProps={{ sx: { p: 0, width: '100%', display: 'block', boxSizing: 'border-box' } }}
                        getRowCanExpand={(row: any) => row.original.__type === 'document' || row.original.__type === 'bin-link-group'}
                        renderDetailPanel={({ row }: { row: any }) => {
                            if (row.original.__type === 'bin-link-group') {
                                return (
                                    <DeletedLinksPanel
                                        doc={row.original.orig}
                                        canUpdate={canUpdate}
                                        onRestoreLink={(linkId) => handleRestoreLink(row.original.orig.slug, linkId)}
                                    />
                                );
                            }
                            if (row.original.__type !== 'document') return null;
                            return (
                                <DocumentLinksPanel
                                    doc={row.original.orig}
                                    entityId={user?.entityId ?? null}
                                    canUpdate={canUpdate}
                                    onEditLink={(link) => setLinkSettings({ documentSlug: row.original.orig.slug, documentTitle: row.original.orig.title, isInvoice: row.original.orig.isInvoice, link })}
                                    onDeactivateLink={(link) => setDeactivateTarget({ documentSlug: row.original.orig.slug, link })}
                                    onOpenLink={openLink}
                                    onCopyLink={copyLink}
                                    onEditInvoiceShare={() => row.original.orig.invoiceId && setInvoiceShareTarget({
                                        id: row.original.orig.invoiceId,
                                        invoiceNumber: row.original.orig.invoiceNumber ?? row.original.orig.title,
                                    })}
                                />
                            );
                        }}
                        {...standardTableProps}
                        // Same header look as table-virtual.tsx (the app's
                        // standard table) rather than the transparent
                        // header the nested link/viewer tables use.
                        muiTableHeadCellProps={{ sx: { fontWeight: 600, fontSize: 12, color: 'var(--color-default-text)', backgroundColor: 'var(--color-container-background)', border: 'none' } }}
                        muiTableBodyRowProps={({ row }: { row: any }) => ({
                            onClick: row.original.__type === 'folder' ? () => setCurrentFolderId(row.original.id) : undefined,
                            sx: {
                                cursor: row.original.__type === 'folder' ? 'pointer' : 'default',
                                backgroundColor: row.index % 2 !== 0 ? 'var(--color-container-background)' : 'var(--color-page-background)',
                            },
                        })}
                    />
                    </Paper>
                )}
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && canCreateFolderHere && (
                <>
                    <Fab
                        onClick={(e) => setFabMenuAnchor(e.currentTarget)}
                        sx={{
                            position: 'fixed',
                            bottom: 20,
                            right: 20,
                            background: 'var(--color-primary)',
                            color: '#fff',
                            '&:hover': { background: 'var(--color-primary-hover)' },
                            zIndex: 999,
                        }}
                    >
                        <Plus size={24} />
                    </Fab>
                    <MuiMenu
                        anchorEl={fabMenuAnchor}
                        open={Boolean(fabMenuAnchor)}
                        onClose={() => setFabMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <MenuItem
                            disabled={!canCreate || !canUploadHere}
                            onClick={() => { setUploadOpen(true); setFabMenuAnchor(null); }}
                            sx={{ display: 'flex', gap: 1 }}
                        >
                            <Upload size={16} color="var(--color-primary)" />
                            Add Document
                        </MenuItem>
                        <MenuItem
                            disabled={!canCreate || currentDepth >= MAX_FOLDER_DEPTH}
                            onClick={() => { setFolderNameOpen({ mode: 'create' }); setFabMenuAnchor(null); }}
                            sx={{ display: 'flex', gap: 1 }}
                        >
                            <FolderPlus size={16} color="var(--color-primary)" />
                            New Folder
                        </MenuItem>
                    </MuiMenu>
                </>
            )}
        </div>
    );
};

export default Documents;
